import groupFolderMapping from '../../config/group-folder-mapping.json';
import packageJson from '../../package.json';
import type { GroupFolderMapping } from '@/types';

// cdk.json の context.ragAdminUi.allowedExtensions と同期されるデフォルト拡張子リスト（単一のマスター定数）
export const DEFAULT_ALLOWED_EXTENSIONS = '.pdf,.html,.htm,.xml,.xslt,.md,.csv,.xls,.xlsx,.json,.rtf,.ppt,.pptx,.doc,.docx,.txt';

// 環境変数から設定を読み込み
export const appConfig = {
  // アプリケーションバージョン
  version: packageJson.version,

  // AWS設定
  region: import.meta.env.VITE_APP_REGION || 'ap-northeast-1',
  userPoolId: import.meta.env.VITE_APP_USER_POOL_ID || '',
  userPoolClientId: import.meta.env.VITE_APP_USER_POOL_CLIENT_ID || '',
  identityPoolId: import.meta.env.VITE_APP_IDENTITY_POOL_ID || '',

  // API設定
  apiEndpoint: import.meta.env.VITE_APP_API_ENDPOINT || '',

  // S3設定
  s3Bucket: import.meta.env.VITE_APP_S3_BUCKET || '',

  // Kendra設定
  kendraIndexId: import.meta.env.VITE_APP_KENDRA_INDEX_ID || '',
  kendraDataSourceId: import.meta.env.VITE_APP_KENDRA_DATA_SOURCE_ID || '',

  // ファイル制限設定
  maxFileSizeMB: Number(import.meta.env.VITE_APP_MAX_FILE_SIZE_MB) || 50,
  allowedExtensions: (import.meta.env.VITE_APP_ALLOWED_EXTENSIONS || DEFAULT_ALLOWED_EXTENSIONS)
    .split(',')
    .map((ext: string) => ext.trim().toLowerCase())
    .filter(Boolean),

  // グループフォルダマッピング
  enableFolderSplit: groupFolderMapping.length > 0,
  groupFolderMapping: groupFolderMapping as GroupFolderMapping[],
};

/**
 * 拡張子からMIMEタイプへの一元管理マッピングテーブル
 * 設定ソース(cdk.json -> .env)の全拡張子に対応
 */
export const EXTENSION_MIME_MAP: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.html': 'text/html',
  '.htm': 'text/html',
  '.xml': 'application/xml',
  '.xslt': 'application/xslt+xml',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.rtf': 'application/rtf',
};

/**
 * 拡張子に対応するMIMEタイプを取得（未定義時は application/octet-stream）
 */
export function getMimeType(ext: string): string {
  const normalizedExt = ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
  return EXTENSION_MIME_MAP[normalizedExt] || 'application/octet-stream';
}

/**
 * ユーザーグループに基づいてアクセス可能なフォルダを取得
 */
export function getAccessibleFolders(userGroups: string[]): GroupFolderMapping[] {
  if (!appConfig.enableFolderSplit) {
    return [
      {
        label: '全ドキュメント (RAG共通)',
        value: 'shared',
        group: '*',
      },
    ];
  }

  return appConfig.groupFolderMapping.filter(mapping =>
    mapping.group === '' || userGroups.includes(mapping.group)
  );
}

/**
 * フォルダのベースパスを取得
 * 形式: docs/<グループ名>/docs/
 */
export function getFolderBasePath(groupValue: string): string {
  if (!appConfig.enableFolderSplit) {
    return 'docs/';
  }
  return `docs/${groupValue}/docs/`;
}

/**
 * ファイルサイズのバリデーション
 */
export function validateFileSize(file: File): boolean {
  const maxSizeBytes = appConfig.maxFileSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
}

/**
 * ファイル拡張子のバリデーション
 */
export function validateFileExtension(file: File): boolean {
  const fileName = file.name;
  const lastDotIndex = fileName.lastIndexOf('.');

  // ドットがない、またはファイル名の先頭にある場合は拡張子なしと判定
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return false;
  }

  const ext = fileName.substring(lastDotIndex).toLowerCase();
  return appConfig.allowedExtensions.includes(ext);
}

/**
 * ファイルサイズを人間が読みやすい形式に変換
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * 日付文字列を Date に変換する。
 *
 * CloudWatch Logs Insights の @timestamp は `YYYY-MM-DD HH:mm:ss.SSS`
 * 形式（UTCだがタイムゾーン記号なし）で返るため、そのまま new Date() に
 * 渡すとブラウザのローカルタイムと誤解釈されJSTへ変換されない。
 * タイムゾーン情報を持たない文字列はUTCとみなして補正する。
 */
function parseDate(dateString: string): Date {
  // ISO8601のタイムゾーン指定（Z または ±HH:MM）が末尾にあればそのまま解釈
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateString.trim());
  if (hasTimezone) {
    return new Date(dateString);
  }

  // タイムゾーンなし → UTCとして解釈する。
  // スペース区切り（CloudWatch形式）を ISO の 'T' 区切りに正規化し 'Z' を付与
  const normalized = dateString.trim().replace(' ', 'T');
  return new Date(`${normalized}Z`);
}

/**
 * 日付を日本語形式（JST固定）にフォーマット
 */
export function formatDate(dateString: string): string {
  if (!dateString) return '-';

  const date = parseDate(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
